import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  Pencil,
  Trash2,
  Key,
  Loader2
} from 'lucide-react';
import {
  usersService,
  AdminUserDetailRecord,
  AdminUserEventRecord
} from '@/services/users';
import { PaginationMeta } from '@/services/agencies';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DetailPageSkeleton, TableSkeleton } from '@/components/skeletons';
import { RelativeTime } from '@/components/RelativeTime';
import { TruncatedValue } from '@/components/TruncatedValue';
import { UserActivityTimeline } from './components/UserActivityTimeline';
import { UserEditModal } from './components/UserEditModal';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import toastService from '@/services/toast/service';

import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer';



export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<AdminUserDetailRecord | null>(null);
  
  // Activity tab state
  const [events, setEvents] = useState<AdminUserEventRecord[]>([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsMeta, setEventsMeta] = useState<PaginationMeta | null>(null);
  const [isEventsLoading, setIsEventsLoading] = useState(false);
  const [isFetchingMoreEvents, setIsFetchingMoreEvents] = useState(false);
  const eventsObserverRef = useRef<IntersectionObserver | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const isViewer = useIsViewer();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Load user detail
  const fetchUser = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const userData = await usersService.getUser(id);
      setUser(userData);
    } catch (error) {
      console.error("Failed to load user details", error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Load events
  const loadEvents = useCallback(async (pageNum: number, append: boolean = false) => {
    if (!id) return;
    if (append) {
      setIsFetchingMoreEvents(true);
    } else {
      setIsEventsLoading(true);
    }
    try {
      const result = await usersService.getUserEvents(id, {
        page: pageNum,
        limit: 20
      });
      setEvents(prev => append ? [...prev, ...result.data] : result.data);
      setEventsMeta(result.meta);
    } catch (error) {
      console.error("Failed to load user events", error);
    } finally {
      setIsEventsLoading(false);
      setIsFetchingMoreEvents(false);
    }
  }, [id]);

  useEffect(() => { loadEvents(1, false); }, [loadEvents]);

  const hasMoreEventPages = eventsMeta ? eventsPage < eventsMeta.totalPages : false;

  // Infinite scroll observer for events — only when there are more pages
  const eventsSentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (isFetchingMoreEvents) return;
    if (eventsObserverRef.current) eventsObserverRef.current.disconnect();

    if (node && hasMoreEventPages) {
      eventsObserverRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          const nextPage = eventsPage + 1;
          setEventsPage(nextPage);
          loadEvents(nextPage, true);
        }
      }, { rootMargin: '200px' });
      eventsObserverRef.current.observe(node);
    }
  }, [isFetchingMoreEvents, hasMoreEventPages, eventsPage, loadEvents]);




  if (isLoading || !user) {
    return <DetailPageSkeleton />;
  }

  const filteredSubaccounts = user.subaccounts.filter(sub => {
    const parentAgency = user.agencies.find(a => a.id === sub.agencyId);
    if (parentAgency && (parentAgency.role === 'super_admin' || parentAgency.role === 'admin' || parentAgency.role === 'owner')) {
      return false;
    }
    return true;
  });

  return (
    <div className="space-y-6 w-full animate-in fade-in duration-300 pb-12">
      {/* Header section */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => navigate('/users')} className="h-9 w-9">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{user.firstName} {user.lastName}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button variant="outline" size="sm" disabled={isViewer} onClick={() => setEditModalOpen(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit User
                  </Button>
                </span>
              </TooltipTrigger>
              {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button variant="outline" size="sm" disabled={isViewer} onClick={() => setResetPasswordModalOpen(true)}>
                    <Key className="mr-2 h-4 w-4" />
                    Reset Password
                  </Button>
                </span>
              </TooltipTrigger>
              {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button variant="destructive" size="sm" disabled={isViewer} onClick={() => setDeleteModalOpen(true)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete User
                  </Button>
                </span>
              </TooltipTrigger>
              {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
            </Tooltip>
          </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">
            Activity Log
          </TabsTrigger>
        </TabsList>

        {/* ────────────────────────────────────────────── */}
        {/* OVERVIEW TAB */}
        {/* ────────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* User Info (Left Column) */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="py-0 gap-0 shadow-sm">
                <CardHeader className="px-5 py-4 gap-0 border-b">
                  <CardTitle className="text-base">User Profile</CardTitle>
                  <CardDescription>Core details and settings</CardDescription>
                </CardHeader>
                <CardContent className="p-5">
                  <div className="space-y-4 text-sm">
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Name</span>
                      <span className="font-medium text-right">{user.firstName} {user.lastName}</span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Email</span>
                      <div className="max-w-[65%] flex-1 flex flex-col items-end min-w-0">
                        <TruncatedValue value={user.email} wrapperClassName="w-full flex justify-end min-w-0 font-medium" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Joined At</span>
                      <span className="font-medium text-right">
                        <RelativeTime date={user.createdAtUtc} />
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground shrink-0">Timezone</span>
                      <span className="font-medium text-right flex items-center gap-1.5">
                        {user.timezone || 'Not set'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Workspaces (Right Column) */}
            <div className="lg:col-span-1">
              <Card className="py-0 gap-0 shadow-sm h-full">
                <CardHeader className="border-b px-5 py-4 flex flex-row items-center justify-between gap-0">
                  <div className="flex flex-col gap-1.5">
                    <CardTitle className="text-base">Workspaces</CardTitle>
                    <CardDescription>Agencies and subaccounts</CardDescription>
                  </div>
                  <Badge variant="secondary" className="font-normal mt-0">
                    {user.agencies.length + filteredSubaccounts.length} Total
                  </Badge>
                </CardHeader>
                <CardContent className="p-0 divide-y">
                  {user.agencies.length === 0 && filteredSubaccounts.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground flex flex-col items-center">
                      <Building2 className="h-8 w-8 opacity-20 mb-3" />
                      <p className="font-medium">No Workspaces</p>
                      <p className="text-xs mt-1">This user doesn't belong to any agencies or subaccounts.</p>
                    </div>
                  ) : (
                    <>
                      {user.agencies.map((agency) => (
                        <div 
                          key={`agency-${agency.id}`} 
                          className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors cursor-pointer group"
                          onClick={() => navigate(`/agencies/${agency.id}`)}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="font-semibold text-sm text-foreground truncate">{agency.name}</span>
                            <span className="text-xs text-muted-foreground mt-0.5">Agency</span>
                          </div>
                          <div className="shrink-0 flex items-center gap-3">
                            <span className="text-sm font-medium text-foreground capitalize">
                              {agency.role ? agency.role.replace('_', ' ') : 'Member'}
                            </span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground opacity-30 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}

                      {filteredSubaccounts.map((sub) => (
                        <div key={`sub-${sub.id}`} className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                          <div className="flex flex-col min-w-0">
                            <span className="font-semibold text-sm text-foreground truncate">{sub.name}</span>
                            <span className="text-xs text-muted-foreground mt-0.5 truncate">Subaccount of {sub.agencyName}</span>
                          </div>
                          <div className="shrink-0">
                            <span className="text-sm font-medium text-foreground capitalize">
                              {sub.role ? sub.role.replace('_', ' ') : 'Member'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ────────────────────────────────────────────── */}
        {/* ACTIVITY TAB */}
        {/* ────────────────────────────────────────────── */}
        <TabsContent value="activity">
          <div className="rounded-lg border bg-card p-4 sm:p-6">
            {isEventsLoading ? (
              <div className="space-y-6">
                <TableSkeleton columns={1} rows={5} />
              </div>
              ) : (
                <>
                  <UserActivityTimeline 
                    events={events} 
                    emptyMessage="No activity recorded for this user yet."
                  />

                  {/* Sentinel element for infinite scroll — only when there are more pages */}
                  {hasMoreEventPages && (
                    <div ref={eventsSentinelRef} className="flex items-center justify-center py-4">
                      {isFetchingMoreEvents && <Spinner size="sm" />}
                    </div>
                  )}
                </>
              )}
              
              {eventsMeta && (
                <div className="mt-6 pt-4 border-t flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing <span className="font-medium text-foreground">{events.length}</span> of{' '}
                    <span className="font-medium text-foreground">{eventsMeta.total}</span> events
                  </p>
                </div>
              )}
          </div>
        </TabsContent>
        
      </Tabs>

      {/* Edit User Modal */}
      <UserEditModal
        userId={user.id}
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSuccess={fetchUser}
      />

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Are you sure you want to delete user <strong className="break-all">{user.firstName} {user.lastName}</strong> ({user.email})?
              </p>
              <div className="bg-destructive/10 border-l-4 border-destructive p-3 text-destructive rounded-r text-sm">
                <strong>Warning:</strong> This action is irreversible. If this user is the only Super Admin for any agencies, those agencies and all their data will also be permanently deleted.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={async () => {
                setIsDeleting(true);
                try {
                  await usersService.deleteUser(user.id);
                  toastService.success(`"${user.firstName} ${user.lastName}" deleted`);
                  navigate('/users');
                } catch (err: any) {
                  toastService.error(err.response?.data?.message || "Couldn't delete user");
                } finally {
                  setIsDeleting(false);
                  setDeleteModalOpen(false);
                }
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete User'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation Modal */}
      <AlertDialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send a password reset email to <strong>{user.email}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <Button
              onClick={async () => {
                setIsResetting(true);
                try {
                  await usersService.resetUserPassword(user.id);
                  toastService.success(`Password reset email sent to ${user.email}`);
                } catch (err: any) {
                  toastService.error(err.response?.data?.message || "Couldn't send reset email");
                } finally {
                  setIsResetting(false);
                  setResetPasswordModalOpen(false);
                }
              }}
              disabled={isResetting}
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Email'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
